import {
  SvgArchive,
  SvgCopy,
  SvgDomain,
  SvgEdit,
  SvgFolder,
  SvgTrash,
} from '@loomhq/lens';
import _uniq from 'lodash/uniq';
import React from 'react';

import { openModal } from 'actions/modals';
import { renameCard, updateCheckedItems } from 'actions/videosDashboard';
import {
  BULK_FOLDER_PUBLISH_TOGGLED,
  BULK_VIDEO_PUBLISH_TOGGLED,
} from 'constants/events';
import * as menuOptions from 'constants/menuOptions';
import {
  CONFIRM_DELETE_ITEM_MODAL,
  DESTINATION_MOVE_MODAL,
  UNPUBLISH_FOLDER_CONFIRMATION_MODAL,
} from 'constants/modalTypes';
import { PROFILE_CONTEXT } from 'constants/videoCard';
import { FolderVisibility, VideoVisibility } from 'constants/visibility';
import { archiveFoldersGraphQL } from 'creators/archiveFolders';
import { archiveVideosGraphQL } from 'creators/archiveVideos';
import { duplicateFolderGraphQL, publishFoldersGraphQL } from 'creators/folder';
import { duplicateVideoGraphQL, publishVideosGraphQL } from 'creators/video';
import { track } from 'utilities/analytics';
import {
  MenuOption,
  OnClickParameters,
  REGULAR_USER_FOLDER,
  REGULAR_USER_VIDEO,
  ShowParameters,
} from 'utilities/menuOptions/types';

import { VIDEO_PRIVACY_OWNER } from '../../../../../shared/constants/video';

const DELETE: MenuOption = {
  key: menuOptions.DELETE,
  label: menuOptions.DELETE,
  icon: <SvgTrash />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const folders = items.filter(
      item => item.__typename === REGULAR_USER_FOLDER
    );
    const videos = items.filter(item => item.__typename === REGULAR_USER_VIDEO);

    dispatch(openModal(CONFIRM_DELETE_ITEM_MODAL, { folders, videos }));
    dispatch(updateCheckedItems([]));
  },
  show: ({
    menuContext,
    items,
    scopes,
    currentUser,
    selectedWorkspace,
  }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      items.every(item => {
        switch (item.__typename) {
          case REGULAR_USER_VIDEO:
            return (
              scopes.hasVideoToolsAccessScope &&
              (item.current_user_is_owner || item.currentUserCanEdit)
            );
          case REGULAR_USER_FOLDER:
            return (
              item.currentUserCanEdit &&
              item.special_id === null &&
              currentUser.id === item.owner_id &&
              scopes.hasTeamLibraryWriteScope &&
              Number(selectedWorkspace.id) === item.organization_id
            );
          default:
            return false;
        }
      })
    );
  },
};

const MOVE: MenuOption = {
  key: menuOptions.MOVE,
  label: menuOptions.MOVE,
  icon: <SvgFolder />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const videos = items.filter(i => i.__typename === 'RegularUserVideo');
    const folders = items.filter(i => i.__typename === 'RegularUserFolder');

    dispatch(openModal(DESTINATION_MOVE_MODAL, { videos, folders }));
  },
  show: ({ items, menuContext, scopes, selectedWorkspace }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasTeamLibraryWriteScope &&
      items.every(
        item =>
          (item.__typename === REGULAR_USER_FOLDER &&
            // Moving allowed for members in the same workspace
            Number(selectedWorkspace.id) === item.organization_id &&
            !item.isArchived) ||
          (item.__typename === REGULAR_USER_VIDEO &&
            // Moving allowed for members in the same workspace
            Number(selectedWorkspace.id) === item.organization_id &&
            !item.archived)
      ) &&
      _uniq(items.map(item => item.owner_id)).length === 1
    );
  },
};

const RENAME: MenuOption = {
  key: menuOptions.RENAME,
  label: menuOptions.RENAME,
  icon: <SvgEdit />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    dispatch(renameCard(items[0].id));
  },
  show: ({ menuContext, items, scopes }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasTeamLibraryWriteScope &&
      items.length === 1 &&
      items.every(
        item =>
          (item.__typename === REGULAR_USER_FOLDER &&
            item.currentUserCanEdit &&
            !item.isArchived) ||
          (item.__typename === REGULAR_USER_VIDEO &&
            item.currentUserCanEdit &&
            !item.archived)
      )
    );
  },
};

const ARCHIVE: MenuOption = {
  key: menuOptions.ARCHIVE,
  label: menuOptions.ARCHIVE,
  icon: <SvgArchive />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const videos = items.filter(i => i.__typename === REGULAR_USER_VIDEO);

    if (videos.length) {
      dispatch(archiveVideosGraphQL(videos, true));
    }

    const folders = items.filter(i => i.__typename === REGULAR_USER_FOLDER);

    if (folders.length) {
      dispatch(archiveFoldersGraphQL(folders, true));
    }

    dispatch(updateCheckedItems([]));
  },
  show: ({ menuContext, scopes, items }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasArchiveAccessScope &&
      items.every(item => {
        switch (item.__typename) {
          case REGULAR_USER_VIDEO:
            return !item.archived && item.currentUserCanEdit;
          case REGULAR_USER_FOLDER:
            return !item.isArchived && item.currentUserCanEdit;
          default:
            false;
        }
      })
    );
  },
};

const UNARCHIVE: MenuOption = {
  key: menuOptions.UNARCHIVE,
  label: menuOptions.UNARCHIVE,
  icon: <SvgArchive />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const videos = items.filter(i => i.__typename === REGULAR_USER_VIDEO);

    if (videos.length) {
      dispatch(archiveVideosGraphQL(videos, false));
    }

    const folders = items.filter(i => i.__typename === REGULAR_USER_FOLDER);

    if (folders.length) {
      dispatch(archiveFoldersGraphQL(folders, false));
    }

    dispatch(updateCheckedItems([]));
  },
  show: ({ menuContext, scopes, items }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasArchiveAccessScope &&
      items.every(item => {
        switch (item.__typename) {
          case REGULAR_USER_VIDEO:
            return item.archived && item.currentUserCanEdit;
          case REGULAR_USER_FOLDER:
            return item.isArchived && item.currentUserCanEdit;
          default:
            false;
        }
      })
    );
  },
};

const DUPLICATE: MenuOption = {
  key: menuOptions.DUPLICATE,
  label: menuOptions.DUPLICATE,
  icon: <SvgCopy />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const item = items[0];
    const newName = `${item.name} (Copy)`;

    if (item.__typename == REGULAR_USER_FOLDER) {
      dispatch(duplicateFolderGraphQL(item.id, newName));
    } else {
      dispatch(duplicateVideoGraphQL(item.id, newName));
    }

    dispatch(updateCheckedItems([]));
  },
  show: ({ menuContext, scopes, items }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      items.length == 1 &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasVideoToolsAccessScope &&
      items.every(item => {
        switch (item.__typename) {
          case REGULAR_USER_VIDEO:
            return !item.archived && item.currentUserCanEdit;
          case REGULAR_USER_FOLDER:
            return !item.isArchived && item.currentUserCanEdit;
          default:
            false;
        }
      })
    );
  },
};

const POST: MenuOption = {
  key: menuOptions.POST,
  label: menuOptions.POST,
  icon: <SvgDomain />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const videoIds = items
      .filter(i => i.__typename === REGULAR_USER_VIDEO)
      .map(v => v.id);

    if (videoIds.length) {
      dispatch(publishVideosGraphQL(videoIds, true));
      track(BULK_VIDEO_PUBLISH_TOGGLED, {
        video_ids: videoIds,
        context_page_path: window.location.pathname,
        toggled_from: VideoVisibility.OWNER,
        toggled_to: VideoVisibility.WORKSPACE,
      });
    }

    const folderIds = items
      .filter(i => i.__typename === REGULAR_USER_FOLDER)
      .map(f => f.id);

    if (folderIds.length) {
      dispatch(publishFoldersGraphQL(folderIds, true, true));
      track(BULK_FOLDER_PUBLISH_TOGGLED, {
        folder_ids: folderIds,
        context_page_path: window.location.pathname,
        toggled_from: FolderVisibility.OWNER,
        toggled_to: FolderVisibility.WORKSPACE,
        should_update_videos: true,
      });
    }

    dispatch(updateCheckedItems([]));
  },
  show: ({ menuContext, scopes, items }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasVideoPublishAccessScope &&
      items.every(item => {
        switch (item.__typename) {
          case REGULAR_USER_VIDEO:
            return (
              !item.archived &&
              item.visibility === VideoVisibility.OWNER &&
              item.currentUserCanEdit &&
              item.privacy !== VIDEO_PRIVACY_OWNER
            );
          case REGULAR_USER_FOLDER:
            return (
              !item.isArchived &&
              item.isTopLevelFolder &&
              item.visibility === FolderVisibility.OWNER &&
              item.currentUserCanEdit
            );
          default:
            false;
        }
      })
    );
  },
};

const UNPOST: MenuOption = {
  key: menuOptions.UNPOST,
  label: menuOptions.UNPOST,
  icon: <SvgDomain />,
  onClick: ({ dispatch, items }: OnClickParameters): void => {
    const videoIds = items
      .filter(i => i.__typename === REGULAR_USER_VIDEO)
      .map(v => v.id);

    if (videoIds.length) {
      dispatch(publishVideosGraphQL(videoIds, false));
      track(BULK_VIDEO_PUBLISH_TOGGLED, {
        video_ids: videoIds,
        context_page_path: window.location.pathname,
        toggled_from: VideoVisibility.WORKSPACE,
        toggled_to: VideoVisibility.OWNER,
      });
    }

    const folders = items.filter(i => i.__typename === REGULAR_USER_FOLDER);

    if (folders.length) {
      dispatch(openModal(UNPUBLISH_FOLDER_CONFIRMATION_MODAL, { folders }));
    }

    dispatch(updateCheckedItems([]));
  },
  show: ({ menuContext, scopes, items }: ShowParameters) => {
    return (
      menuContext.type !== PROFILE_CONTEXT &&
      scopes.hasAuthenticatedAccess &&
      scopes.hasVideoPublishAccessScope &&
      items.every(item => {
        switch (item.__typename) {
          case REGULAR_USER_VIDEO:
            return (
              !item.archived &&
              item.visibility !== VideoVisibility.OWNER &&
              item.currentUserCanEdit
            );
          case REGULAR_USER_FOLDER:
            return (
              !item.isArchived &&
              item.isTopLevelFolder &&
              item.visibility !== FolderVisibility.OWNER &&
              item.currentUserCanEdit
            );
          default:
            false;
        }
      })
    );
  },
};

export default {
  ARCHIVE,
  DELETE,
  DUPLICATE,
  MOVE,
  RENAME,
  UNARCHIVE,
  POST,
  UNPOST,
};
